import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, Users, Eye, Calendar, Star, Clock,
  Loader2, Edit, Trash2, Send, Bell, MessageSquare, Phone, Mail
} from "lucide-react";
import { API } from "@/App";

export const CaseViewingsPage = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [viewings, setViewings] = useState([]);
  const [showViewingModal, setShowViewingModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [editingViewing, setEditingViewing] = useState(null);
  const [selectedViewing, setSelectedViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [viewingForm, setViewingForm] = useState({
    viewer_name: "",
    viewer_email: "",
    viewer_phone: "",
    viewing_date: "",
    viewing_time: "",
    agent_notes: ""
  });

  const [feedbackForm, setFeedbackForm] = useState({
    feedback: "",
    rating: 3
  });

  useEffect(() => {
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [caseRes, viewingsRes] = await Promise.all([
        axios.get(`${API}/sales/cases/${caseId}`, { headers }),
        axios.get(`${API}/portal/viewings/case/${caseId}`, { headers })
      ]);

      setCaseData(caseRes.data.case);
      setViewings(viewingsRes.data.viewings || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateViewing = async () => {
    if (!viewingForm.viewer_name || !viewingForm.viewing_date) {
      toast.error("Please fill in viewer name and date");
      return;
    }

    setSaving(true);
    try {
      if (editingViewing) {
        await axios.put(`${API}/portal/viewings/${editingViewing.viewing_id}`, viewingForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Viewing updated");
      } else {
        await axios.post(`${API}/portal/viewings/create`, {
          ...viewingForm,
          case_id: caseId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Viewing scheduled");
      }

      setShowViewingModal(false);
      resetViewingForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save viewing");
    } finally {
      setSaving(false);
    }
  };

  const handleAddFeedback = async () => {
    if (!feedbackForm.feedback) {
      toast.error("Please enter feedback");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/portal/viewings/${selectedViewing.viewing_id}/feedback`, feedbackForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Feedback added");
      setShowFeedbackModal(false);
      setFeedbackForm({ feedback: "", rating: 3 });
      fetchData();
    } catch (error) {
      toast.error("Failed to add feedback");
    } finally {
      setSaving(false);
    }
  };

  const handleNotifySeller = async (viewing) => {
    try {
      await axios.post(`${API}/portal/viewings/${viewing.viewing_id}/notify-seller`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Seller notified of feedback");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to notify seller");
    }
  };

  const handleDeleteViewing = async (viewingId) => {
    if (!confirm("Are you sure you want to delete this viewing?")) return;

    try {
      await axios.delete(`${API}/portal/viewings/${viewingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Viewing deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete viewing");
    }
  };

  const handleUpdateStatus = async (viewingId, status) => {
    try {
      await axios.put(`${API}/portal/viewings/${viewingId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetViewingForm = () => {
    setViewingForm({
      viewer_name: "",
      viewer_email: "",
      viewer_phone: "",
      viewing_date: "",
      viewing_time: "",
      agent_notes: ""
    });
    setEditingViewing(null);
  };

  const openEditViewing = (viewing) => {
    setViewingForm({
      viewer_name: viewing.viewer_name || "",
      viewer_email: viewing.viewer_email || "",
      viewer_phone: viewing.viewer_phone || "",
      viewing_date: viewing.viewing_date?.split("T")[0] || "",
      viewing_time: viewing.viewing_time || "",
      agent_notes: viewing.agent_notes || ""
    });
    setEditingViewing(viewing);
    setShowViewingModal(true);
  };

  const openFeedbackModal = (viewing) => {
    setSelectedViewing(viewing);
    setFeedbackForm({
      feedback: viewing.feedback || "",
      rating: viewing.feedback_rating || 3
    });
    setShowFeedbackModal(true);
  };

  // Stats
  const stats = {
    total: viewings.length,
    scheduled: viewings.filter(v => v.status === "scheduled").length,
    completed: viewings.filter(v => v.status === "completed").length,
    withFeedback: viewings.filter(v => v.feedback).length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/sales/case/${caseId}`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Viewings & Feedback</h1>
                <p className="text-sm text-muted-foreground">
                  {caseData?.address_line1}, {caseData?.postcode}
                </p>
              </div>
            </div>
            <Button onClick={() => { resetViewingForm(); setShowViewingModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Schedule Viewing
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total Viewings</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.scheduled}</p>
              <p className="text-sm text-muted-foreground">Scheduled</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{stats.withFeedback}</p>
              <p className="text-sm text-muted-foreground">With Feedback</p>
            </CardContent>
          </Card>
        </div>

        {/* Viewings List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Viewings ({viewings.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {viewings.length === 0 ? (
              <div className="text-center py-8">
                <Eye className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No viewings scheduled</p>
                <Button className="mt-4" onClick={() => setShowViewingModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule First Viewing
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {viewings.map((viewing) => (
                  <div key={viewing.viewing_id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="font-semibold">{viewing.viewer_name}</p>
                          <Badge className={
                            viewing.status === "completed" ? "bg-green-100 text-green-700" :
                            viewing.status === "cancelled" ? "bg-red-100 text-red-700" :
                            viewing.status === "no_show" ? "bg-slate-100 text-slate-700" :
                            "bg-blue-100 text-blue-700"
                          }>
                            {viewing.status?.replace(/_/g, " ").toUpperCase() || "SCHEDULED"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {viewing.viewing_date ? new Date(viewing.viewing_date).toLocaleDateString() : "No date"}
                            {viewing.viewing_time && ` at ${viewing.viewing_time}`}
                          </span>
                          {viewing.viewer_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {viewing.viewer_email}
                            </span>
                          )}
                          {viewing.viewer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {viewing.viewer_phone}
                            </span>
                          )}
                        </div>

                        {/* Feedback section */}
                        {viewing.feedback && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-1 mb-1">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-4 h-4 ${
                                  i < viewing.feedback_rating ? "text-amber-400 fill-amber-400" : "text-slate-200"
                                }`} />
                              ))}
                              {viewing.seller_notified && (
                                <Badge className="ml-2 bg-green-100 text-green-700">
                                  <Bell className="w-3 h-3 mr-1" />
                                  Seller Notified
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">{viewing.feedback}</p>
                          </div>
                        )}

                        {viewing.agent_notes && (
                          <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded mt-2">
                            <strong>Notes:</strong> {viewing.agent_notes}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        {viewing.status === "scheduled" && (
                          <Select onValueChange={(v) => handleUpdateStatus(viewing.viewing_id, v)}>
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                              <SelectItem value="no_show">No Show</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {viewing.status === "completed" && !viewing.feedback && (
                          <Button size="sm" onClick={() => openFeedbackModal(viewing)}>
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Add Feedback
                          </Button>
                        )}
                        {viewing.feedback && !viewing.seller_notified && (
                          <Button size="sm" variant="outline" onClick={() => handleNotifySeller(viewing)}>
                            <Bell className="w-4 h-4 mr-1" />
                            Notify Seller
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openEditViewing(viewing)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => handleDeleteViewing(viewing.viewing_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Schedule Viewing Modal */}
      <Dialog open={showViewingModal} onOpenChange={setShowViewingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingViewing ? "Edit Viewing" : "Schedule Viewing"}</DialogTitle>
            <DialogDescription>
              Enter viewer details and schedule a viewing time.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Viewer Name *</Label>
              <Input
                value={viewingForm.viewer_name}
                onChange={(e) => setViewingForm({ ...viewingForm, viewer_name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={viewingForm.viewer_email}
                  onChange={(e) => setViewingForm({ ...viewingForm, viewer_email: e.target.value })}
                  placeholder="viewer@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={viewingForm.viewer_phone}
                  onChange={(e) => setViewingForm({ ...viewingForm, viewer_phone: e.target.value })}
                  placeholder="07xxx xxx xxx"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={viewingForm.viewing_date}
                  onChange={(e) => setViewingForm({ ...viewingForm, viewing_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={viewingForm.viewing_time}
                  onChange={(e) => setViewingForm({ ...viewingForm, viewing_time: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Agent Notes (internal)</Label>
              <Textarea
                value={viewingForm.agent_notes}
                onChange={(e) => setViewingForm({ ...viewingForm, agent_notes: e.target.value })}
                placeholder="Any notes about this viewer..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewingModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateViewing} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingViewing ? "Update" : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Modal */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Viewing Feedback</DialogTitle>
            <DialogDescription>
              Record feedback from {selectedViewing?.viewer_name}&apos;s viewing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setFeedbackForm({ ...feedbackForm, rating: num })}
                    className="focus:outline-none"
                  >
                    <Star className={`w-8 h-8 transition-colors ${
                      num <= feedbackForm.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"
                    }`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Feedback *</Label>
              <Textarea
                value={feedbackForm.feedback}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback: e.target.value })}
                placeholder="What did the viewer think of the property?"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddFeedback} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseViewingsPage;
